/**
 * Lebensmittel in der Seitenansicht: Fleischstück mit Kruste, Pfannkuchen,
 * kleine Würfel und Teig. Rohes ist hell, Gebräuntes dunkel – so liest man
 * den Garzustand ohne Farbe.
 */
import { random } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { blobPoints, ellipsePath, path, roundedRectPath, smooth } from '../paths.ts';
import { circle, group, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';

/**
 * Fleischstück als abgerundeter Block mit Kruste oben und unten.
 * @param crustTop Dicke der Kruste oben, 0 bis 1 (Anteil der halben Höhe).
 * @param crustBottom Dicke der Kruste unten.
 */
export function meatPiece(
  cx: number,
  cy: number,
  width: number,
  height: number,
  crustTop: number,
  crustBottom: number,
): SceneNode {
  const outline = roundedRectPath(cx - width / 2, cy - height / 2, width, height, Math.min(7, height * 0.45));
  const band = (fromTop: boolean, amount: number): SceneNode | null => {
    if (amount <= 0.01) {
      return null;
    }
    const thickness = (height / 2) * amount;
    const y = fromTop ? cy - height / 2 : cy + height / 2 - thickness;
    return rect(cx - width / 2 - 2, y, width + 4, thickness, 0, styles.flat('deep'));
  };
  // Fettmaserung nur bei großen Stücken, auf kleinen wirkt sie unruhig.
  const fat =
    width < 36
      ? null
      : shape(
          smooth(
            [
              { x: cx - width * 0.3, y: cy + 1 },
              { x: cx - width * 0.05, y: cy - 2 },
              { x: cx + width * 0.25, y: cy + 1.5 },
            ],
            false,
          ),
          { stroke: 'paper', strokeWidth: STROKE.detail },
        );
  return group([
    shape(outline, styles.flat('shade')),
    group([fat, band(true, crustTop), band(false, crustBottom)], { clip: outline }),
    shape(outline, styles.outline()),
  ]);
}

/**
 * Pfannkuchen von der Seite: flache Scheibe mit Oberseite und Rand.
 * @param flip Drehung um die Querachse in Grad. 0 zeigt Seite A oben,
 *             180 Seite B. Dazwischen wird die Scheibe gestaucht.
 */
export function pancake(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  flip: number,
  sideA: Tone,
  sideB: Tone,
): SceneNode {
  const radians = (flip * Math.PI) / 180;
  const cos = Math.cos(radians);
  const thickness = 4;
  const showA = cos >= 0;
  const face = Math.max(0.12, Math.abs(cos));
  // Die sichtbare Fläche ist die obere Seite; der Rand liegt darunter.
  const top: Tone = showA ? sideA : sideB;
  const spots: SceneNode[] =
    top === 'shade'
      ? [
          circle(cx - rx * 0.4, cy - ry * 0.15, 2.6, styles.flat('deep')),
          circle(cx + rx * 0.1, cy + ry * 0.35, 2.2, styles.flat('deep')),
          circle(cx + rx * 0.45, cy - ry * 0.2, 2.4, styles.flat('deep')),
          circle(cx - rx * 0.05, cy - ry * 0.45, 1.8, styles.flat('deep')),
          circle(cx - rx * 0.55, cy + ry * 0.35, 1.6, styles.flat('deep')),
        ]
      : [];
  return group(
    [
      shape(roundedRectPath(cx - rx, cy, rx * 2, thickness, 2), styles.solid(showA ? sideB : sideA)),
      shape(ellipsePath(cx, cy, rx, ry), styles.solid(top)),
      group(spots, { clip: ellipsePath(cx, cy, rx - 2, ry - 2) }),
    ],
    { transform: { scaleY: face, ox: cx, oy: cy + thickness / 2 } },
  );
}

/** Lage eines kleinen Würfels. */
export interface Cube {
  readonly x: number;
  readonly y: number;
  readonly turn: number;
}

/** Zufällig verteilte kleine Stücke in einer Ellipse, fest über den Startwert. */
export function scatter(cx: number, cy: number, rx: number, ry: number, count: number, seed: number): Cube[] {
  const rng = random(seed);
  const cubes: Cube[] = [];
  for (let index = 0; index < count; index += 1) {
    const angle = rng() * Math.PI * 2;
    const distance = Math.sqrt(rng());
    cubes.push({
      x: cx + Math.cos(angle) * rx * distance,
      y: cy + Math.sin(angle) * ry * distance,
      turn: (rng() - 0.5) * 50,
    });
  }
  return cubes.sort((a, b) => a.y - b.y);
}

/** Ein kleiner Würfel. */
export function cube(
  position: Point & { turn: number },
  size: number,
  fill: Tone,
  strokeWidth: number = STROKE.detail,
): SceneNode {
  return group(
    [
      rect(position.x - size / 2, position.y - size / 2, size, size, 1.2, {
        fill,
        stroke: 'ink',
        strokeWidth,
      }),
    ],
    {
      transform: { rotate: position.turn, ox: position.x, oy: position.y },
    },
  );
}

/** Teigkugel: weich gewölbt, unten abgeflacht. squash 1 = rund, kleiner = flacher. */
export function doughBall(cx: number, bottomY: number, width: number, height: number, seed = 5): string {
  const rng = random(seed);
  const wobble = Array.from({ length: 14 }, () => 0.97 + rng() * 0.05);
  const points = blobPoints(cx, bottomY - height / 2, width / 2, height / 2, 14, (angle, index) =>
    Math.sin(angle) > 0.55 ? 1 : (wobble[index] ?? 1),
  ).map((point) => ({ x: point.x, y: Math.min(point.y, bottomY) }));
  return smooth(points, true, 0.9);
}

/**
 * Kuchen von der Seite: Körper mit gewölbter Oberseite. Die Oberseite ist
 * gebräunt (Kruste), die Seite heller.
 * @param bottomY Unterkante; height ist die Höhe am Rand, dome die Wölbung.
 * @param crustTone Ton der Oberseite; roher Teig ist hell und hat keinen Riss.
 */
export function cake(
  cx: number,
  bottomY: number,
  width: number,
  height: number,
  dome: number,
  crustTone: Tone = 'shade',
): SceneNode[] {
  const rx = width / 2;
  const ry = Math.max(4, width * 0.16);
  const top = bottomY - height;
  const side = path()
    .move(cx - rx, top)
    .line(cx - rx, bottomY)
    .arc(rx, ry, false, false, cx + rx, bottomY)
    .line(cx + rx, top)
    .close()
    .toString();
  const crust = path()
    .move(cx - rx, top)
    .curve(cx - rx * 0.9, top - ry - dome * 1.6, cx + rx * 0.9, top - ry - dome * 1.6, cx + rx, top)
    .arc(rx, ry, false, true, cx - rx, top)
    .close()
    .toString();
  if (crustTone !== 'shade') {
    return [shape(side, styles.solid('tint')), shape(crust, styles.solid(crustTone))];
  }
  return [
    shape(side, styles.solid('tint')),
    shape(crust, styles.solid('shade')),
    shape(
      path()
        .move(cx - rx * 0.35, top - ry * 0.4 - dome)
        .quad(cx, top - ry * 0.9 - dome * 1.1, cx + rx * 0.3, top - ry * 0.5 - dome)
        .toString(),
      {
        stroke: 'deep',
        strokeWidth: STROKE.detail,
      },
    ),
  ];
}
